(() => {
  if (window.LoshenArrivalsNavigation) return;
  window.LoshenArrivalsNavigation = true;

  const rootId = 'lc-arrivals-products';
  const collectionPath = window.location.pathname;
  const getRoot = () => document.getElementById(rootId);
  const reducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let controller;
  let requestNumber = 0;

  const styles = document.createElement('style');

  styles.textContent = `
    #lc-arrivals-products .lcna-product-grid {
      transition: opacity .18s ease;
    }

    #lc-arrivals-products[aria-busy="true"] .lcna-product-grid {
      opacity: .45;
      pointer-events: none;
    }

    #lc-arrivals-products[aria-busy="true"] {
      cursor: progress;
    }

    @media (prefers-reduced-motion: reduce) {
      #lc-arrivals-products .lcna-product-grid {
        transition: none;
      }
    }
  `;

  document.head.append(styles);

  const status = document.createElement('div');
  status.className = 'sr-only';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');
  document.body.append(status);

  const canUpdate = url => {
    const root = getRoot();

    return root &&
      root.closest('[id^="shopify-section-"]') &&
      url.origin === window.location.origin &&
      url.pathname === collectionPath;
  };

  const updateProducts = async (
    url,
    mode = 'filter',
    addHistory = true
  ) => {
    const current = getRoot();
    const wrapper = current?.closest('[id^="shopify-section-"]');

    if (!current || !wrapper) return;

    controller?.abort();
    controller = new AbortController();

    const activeController = controller;
    const request = ++requestNumber;
    let timedOut = false;

    const timeout = window.setTimeout(() => {
      timedOut = true;
      activeController.abort();
    }, 10000);

    current.setAttribute('aria-busy', 'true');

    const fetchUrl = new URL(url.href);
    fetchUrl.hash = '';

    const sectionId = encodeURIComponent(
      wrapper.id.replace('shopify-section-', '')
    );

    const sectionUrl = fetchUrl.href +
      (fetchUrl.search ? '&' : '?') +
      'section_id=' +
      sectionId;

    try {
      const response = await fetch(sectionUrl, {
        credentials: 'same-origin',
        signal: activeController.signal,
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to load products');
      }

      const html = await response.text();

      if (request !== requestNumber) return;

      const parsed = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const result = parsed.getElementById(rootId);

      if (!result) {
        throw new Error('Product section is missing');
      }

      const next = document.importNode(result, true);
      const scrollY = window.scrollY;

      const tabsScroll =
        current.querySelector('.lcna-tabs')?.scrollLeft || 0;

      const wasOpen =
        current.querySelector('.lcna-refine')?.open;

      const focused = document.activeElement;
      const restoreFocus = current.contains(focused);
      const focusedId = focused?.id;

      if (wasOpen && next.querySelector('.lcna-refine')) {
        next.querySelector('.lcna-refine').open = true;
      }

      current.replaceWith(next);

      const nextTabs = next.querySelector('.lcna-tabs');

      if (nextTabs) {
        nextTabs.scrollLeft = tabsScroll;
      }

      if (addHistory && url.href !== window.location.href) {
        window.history.pushState(
          window.history.state,
          '',
          url.href
        );
      }

      if (restoreFocus) {
        let focusTarget = focusedId
          ? document.getElementById(focusedId)
          : null;

        if (!focusTarget && mode === 'category') {
          focusTarget = next.querySelector(
            '.lcna-tabs [aria-current="page"]'
          );
        }

        if (!focusTarget) {
          focusTarget = next.querySelector(
            '.lcna-shop__head h2'
          );

          focusTarget?.setAttribute('tabindex', '-1');
        }

        focusTarget?.focus({
          preventScroll: true
        });
      }

      if (mode === 'page') {
        next.scrollIntoView({
          behavior: reducedMotion() ? 'instant' : 'smooth',
          block: 'start'
        });
      } else if (mode !== 'history') {
        window.scrollTo({
          top: scrollY,
          behavior: 'instant'
        });
      }

      const grid = next.querySelector('.lcna-product-grid');

      if (grid?.animate && !reducedMotion()) {
        grid.animate(
          [
            { opacity: .45 },
            { opacity: 1 }
          ],
          {
            duration: 180,
            easing: 'ease-out'
          }
        );
      }

      status.textContent =
        next.querySelector('.lcna-shop__head p')
          ?.textContent.trim() || '';

    } catch (error) {
      if (request !== requestNumber) return;

      if (activeController.signal.aborted && !timedOut) {
        return;
      }

      window.location.assign(url.href);

    } finally {
      window.clearTimeout(timeout);

      if (request === requestNumber) {
        getRoot()?.removeAttribute('aria-busy');
      }
    }
  };

  document.addEventListener('click', event => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) return;

    const link = event.target.closest?.(
      '.lcna-tabs a, .lcna-active a, .lcna-pagination a, ' +
      '.lcna-filter-actions a, .lcna-empty a'
    );

    if (
      !link ||
      !getRoot()?.contains(link) ||
      link.hasAttribute('download') ||
      (link.target && link.target !== '_self')
    ) return;

    const url = new URL(
      link.href,
      window.location.href
    );

    if (!canUpdate(url)) return;

    event.preventDefault();

    const mode = link.closest('.lcna-pagination')
      ? 'page'
      : link.closest('.lcna-tabs')
        ? 'category'
        : 'filter';

    updateProducts(url, mode);
  });

  document.addEventListener('submit', event => {
    const form = event.target;

    if (
      event.defaultPrevented ||
      !form.matches?.('.lcna-refinements') ||
      !getRoot()?.contains(form)
    ) return;

    const url = new URL(window.location.href);

    if (!canUpdate(url)) return;

    event.preventDefault();

    for (const key of new Set(url.searchParams.keys())) {
      if (
        key.startsWith('filter.') ||
        key === 'sort_by' ||
        key === 'page'
      ) {
        url.searchParams.delete(key);
      }
    }

    for (const [key, value] of new FormData(form)) {
      if (
        typeof value === 'string' &&
        value.trim() !== ''
      ) {
        url.searchParams.append(key, value);
      }
    }

    url.hash = rootId;

    updateProducts(url);
  });

  window.addEventListener('popstate', () => {
    const url = new URL(window.location.href);

    if (canUpdate(url)) {
      updateProducts(url, 'history', false);
    }
  });

  const preserveArrivalFilters = event => {
    if (!getRoot()) return;

    const form = event.target.closest?.(
      'form[action$="/localization"]'
    );

    if (!form) return;

    const url = new URL(window.location.href);
    const grouped = new Map();

    for (
      const part of url.search.slice(1).split('&').filter(Boolean)
    ) {
      const separator = part.indexOf('=');

      const key = separator < 0
        ? part
        : part.slice(0, separator);

      const value = separator < 0
        ? ''
        : part.slice(separator + 1);

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }

      grouped.get(key).push(value);
    }

    url.search = Array.from(
      grouped,
      ([key, values]) =>
        key.startsWith('filter.')
          ? key + '=' + values.join(',')
          : values.map(
              value => key + '=' + value
            ).join('&')
    ).join('&');

    let input = form.querySelector(
      'input[name="return_to"]'
    );

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.append(input);
    }

    input.value = url.pathname + url.search;
    input.setAttribute('value', input.value);
  };

  document.addEventListener(
    'change',
    preserveArrivalFilters,
    true
  );

  document.addEventListener(
    'submit',
    preserveArrivalFilters,
    true
  );
})();